import { useEffect, useState } from "react";
import { todos as api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, CheckSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AutoSchedulerModal from "../components/AutoSchedulerModal";
import { isFeatureEnabled } from "../components/FeatureGate";

const priorityColors = {
  high: "bg-[color:var(--accent-soft)] text-[color:var(--accent-ink)]",
  medium: "bg-[color:var(--warning-bg)] text-[color:var(--warning)]",
  low: "bg-[color:var(--primary-soft)] text-[color:var(--success)]",
};

export default function TodoPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("General");
  const [priority, setPriority] = useState("medium");
  const [due, setDue] = useState("");
  const [schedOpen, setSchedOpen] = useState(false);

  async function load() {
    setLoading(true);
    try { setList(await api.list()); } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!title.trim()) return;
    try {
      await api.create({ title, category, priority, due_date: due || null });
      setTitle(""); setDue("");
      load();
    } catch { toast.error("Add failed"); }
  }

  async function toggle(t) {
    try { await api.update(t.id, { completed: !t.completed }); load(); } catch { toast.error("Update failed"); }
  }

  async function remove(id) {
    try { await api.remove(id); load(); } catch { toast.error("Delete failed"); }
  }

  const grouped = list.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="label-caps">Utility</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">To-Do List.</h1>
            <p className="mt-2 text-[color:var(--text-muted)] max-w-xl">A simple, honest list. Categories, priorities, due dates.</p>
          </div>
          {isFeatureEnabled("feature_auto_scheduler") && (
            <button
              onClick={() => setSchedOpen(true)}
              data-testid="open-auto-scheduler"
              className="inline-flex items-center gap-2 px-3 h-10 rounded-lg text-sm font-semibold text-white shrink-0"
              style={{ background: "var(--primary)" }}>
              <Sparkles className="h-4 w-4" /> <span className="hidden sm:inline">Find me time</span>
            </button>
          )}
        </div>
      </div>

      <AutoSchedulerModal open={schedOpen} onClose={() => setSchedOpen(false)} onCommitted={load} />

      <div className="card-tactile rounded-2xl p-5 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-5">
          <label className="label-caps mb-1 block">Task</label>
          <Input data-testid="todo-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Water the plants" onKeyDown={(e) => e.key === "Enter" && add()} />
        </div>
        <div className="md:col-span-3">
          <label className="label-caps mb-1 block">Category</label>
          <Input data-testid="todo-category" value={category} onChange={(e) => setCategory(e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="label-caps mb-1 block">Priority</label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="todo-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className="label-caps mb-1 block">Due</label>
          <Input data-testid="todo-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </div>
        <div className="md:col-span-12">
          <Button onClick={add} data-testid="todo-add" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white w-full md:w-auto">
            <Plus className="h-4 w-4 mr-2" /> Add task
          </Button>
        </div>
      </div>

      {loading ? <div className="text-[color:var(--text-muted)]">Loading…</div> :
        list.length === 0 ? (
          <div className="card-tactile rounded-2xl p-10 flex flex-col items-center text-center" data-testid="empty-todos">
            <div className="h-16 w-16 rounded-full bg-[color:var(--warning-bg)] flex items-center justify-center mb-4"><CheckSquare className="h-7 w-7 text-[color:var(--warning)]" /></div>
            <h3 className="font-display font-bold text-xl">Your list is empty</h3>
            <p className="text-sm text-[color:var(--text-muted)] mt-1">Add your first task above.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <h3 className="label-caps mb-3">{cat} · {items.length}</h3>
                <div className="space-y-2">
                  {items.map((t, i) => (
                    <div key={t.id} data-testid={`todo-row-${t.id}`} className="card-tactile rounded-xl p-4 flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 30}ms` }}>
                      <Checkbox checked={t.completed} onCheckedChange={() => toggle(t)} data-testid={`todo-check-${t.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${t.completed ? "line-through text-[color:var(--text-muted)]" : "text-[color:var(--text-main)]"}`}>{t.title}</div>
                        {t.due_date && <div className="text-xs text-[color:var(--text-muted)] mt-0.5">Due {new Date(t.due_date).toLocaleDateString()}</div>}
                      </div>
                      <span className={`dose-pill ${priorityColors[t.priority] || ""}`}>{t.priority}</span>
                      <button onClick={() => remove(t.id)} data-testid={`todo-delete-${t.id}`} className="text-[color:var(--text-muted)] hover:text-[color:var(--accent-ink)] p-1">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
